import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { HexColorInput, HexColorPicker } from "react-colorful"
import { MdClose, MdColorize, MdStar, MdStarBorder } from "react-icons/md"
import styles from "./colorPicker.module.css"

const defaultColors = ["#ffffff", "#111111", "#ef4444", "#f97316", "#eab308", "#22c55e", "#06b6d4", "#3b82f6", "#8b5cf6", "#ec4899"]
const FAVORITE_COLORS_KEY = "oktto_favorite_colors"
const FAVORITE_COLORS_UPDATED_EVENT = "oktto_favorite_colors_updated"
const MAX_FAVORITE_COLORS = 12
const POPOVER_WIDTH = 248
const POPOVER_MARGIN = 8

const normalizeColor = (value) => {
    const color = `${value || ""}`.trim()
    if (/^#[0-9a-f]{6}$/i.test(color)) return color.toLowerCase()
    if (/^[0-9a-f]{6}$/i.test(color)) return `#${color.toLowerCase()}`
    return "#ffffff"
}

const getFavoriteColors = () => {
    try {
        const colors = JSON.parse(window.localStorage.getItem(FAVORITE_COLORS_KEY) || "[]")
        return Array.isArray(colors) ? colors.map(normalizeColor).filter(Boolean).slice(0, MAX_FAVORITE_COLORS) : []
    } catch (error) {
        window.localStorage.removeItem(FAVORITE_COLORS_KEY)
        return []
    }
}

const ColorPicker = ({ value = "#ffffff", onChange = () => { }, label = "Cor", icon = null, disabled = false, colors = defaultColors, className = "", showValue = true, showLabel = true }) => {
    const [open, setOpen] = useState(false)
    const [favoriteColors, setFavoriteColors] = useState(getFavoriteColors)
    const [popoverStyle, setPopoverStyle] = useState({})
    const wrapperRef = useRef(null)
    const popoverRef = useRef(null)
    const color = normalizeColor(value)
    const isFavorite = favoriteColors.includes(color)

    useEffect(() => {
        if (!open) return

        setFavoriteColors(getFavoriteColors())

        const handlePointerDown = (event) => {
            if (!wrapperRef.current?.contains(event.target)) {
                setOpen(false)
            }
        }

        document.addEventListener("pointerdown", handlePointerDown)
        return () => document.removeEventListener("pointerdown", handlePointerDown)
    }, [open])

    useLayoutEffect(() => {
        if (!open) return

        const updatePopoverPosition = () => {
            const triggerRect = wrapperRef.current?.getBoundingClientRect()
            const popoverRect = popoverRef.current?.getBoundingClientRect()
            if (!triggerRect) return

            const width = popoverRect?.width || POPOVER_WIDTH
            const height = popoverRect?.height || 360
            const maxWidth = Math.max(0, window.innerWidth - (POPOVER_MARGIN * 2))
            const maxHeight = Math.max(0, window.innerHeight - (POPOVER_MARGIN * 2))
            const constrainedWidth = Math.min(width, maxWidth)
            const constrainedHeight = Math.min(height, maxHeight)
            const maxLeft = Math.max(POPOVER_MARGIN, window.innerWidth - constrainedWidth - POPOVER_MARGIN)
            const preferredLeft = triggerRect.left
            const left = Math.min(Math.max(preferredLeft, POPOVER_MARGIN), maxLeft)
            const bottomTop = triggerRect.bottom + POPOVER_MARGIN
            const top = bottomTop + constrainedHeight + POPOVER_MARGIN <= window.innerHeight
                ? bottomTop
                : Math.max(POPOVER_MARGIN, triggerRect.top - constrainedHeight - POPOVER_MARGIN)

            setPopoverStyle({ left, top, maxWidth, maxHeight })
        }

        updatePopoverPosition()
        window.addEventListener("resize", updatePopoverPosition)
        window.addEventListener("scroll", updatePopoverPosition, true)
        return () => {
            window.removeEventListener("resize", updatePopoverPosition)
            window.removeEventListener("scroll", updatePopoverPosition, true)
        }
    }, [open])

    useEffect(() => {
        const handleFavoritesUpdated = () => {
            setFavoriteColors(getFavoriteColors())
        }

        window.addEventListener(FAVORITE_COLORS_UPDATED_EVENT, handleFavoritesUpdated)
        window.addEventListener("storage", handleFavoritesUpdated)
        return () => {
            window.removeEventListener(FAVORITE_COLORS_UPDATED_EVENT, handleFavoritesUpdated)
            window.removeEventListener("storage", handleFavoritesUpdated)
        }
    }, [])

    const handleChange = (nextColor) => {
        onChange(normalizeColor(nextColor))
    }

    const saveFavoriteColors = (nextColors) => {
        const normalizedColors = [...new Set(nextColors.map(normalizeColor))].slice(0, MAX_FAVORITE_COLORS)
        setFavoriteColors(normalizedColors)
        window.localStorage.setItem(FAVORITE_COLORS_KEY, JSON.stringify(normalizedColors))
        window.dispatchEvent(new CustomEvent(FAVORITE_COLORS_UPDATED_EVENT))
    }

    const toggleFavoriteColor = () => {
        if (isFavorite) {
            saveFavoriteColors(favoriteColors.filter(favoriteColor => favoriteColor !== color))
            return
        }

        saveFavoriteColors([color, ...favoriteColors])
    }

    const pickColorFromPage = async () => {
        if (typeof window === "undefined" || !window.EyeDropper) return
        try {
            const result = await new window.EyeDropper().open()
            if (result?.sRGBHex) handleChange(result.sRGBHex)
            setOpen(false)
        } catch (error) {
            if (error?.name !== "AbortError") console.error("Não foi possível usar o conta-gotas.", error)
        }
    }

    const renderColorButton = (preset, { removable = false } = {}) => {
        const normalizedPreset = normalizeColor(preset)

        return (
            <div className={styles.colorOption} key={normalizedPreset}>
                <button
                    type="button"
                    className={normalizedPreset === color ? styles.active : ""}
                    style={{ backgroundColor: normalizedPreset }}
                    onClick={() => handleChange(normalizedPreset)}
                    title={normalizedPreset}
                    aria-label={`Selecionar ${normalizedPreset}`}
                />
                {removable &&
                    <button
                        type="button"
                        className={styles.removeFavorite}
                        onClick={(event) => {
                            event.stopPropagation()
                            saveFavoriteColors(favoriteColors.filter(favoriteColor => favoriteColor !== normalizedPreset))
                        }}
                        title="Remover favorito"
                        aria-label={`Remover ${normalizedPreset} dos favoritos`}
                    >
                        <MdClose />
                    </button>
                }
            </div>
        )
    }

    return (
        <div className={`${styles.colorPicker} ${className}`} ref={wrapperRef}>
            <button
                type="button"
                className={styles.trigger}
                onClick={() => !disabled && setOpen(previousValue => !previousValue)}
                disabled={disabled}
                title={label}
                aria-label={label}
                aria-expanded={open}
            >
                {icon}
                <span className={styles.swatch} style={{ backgroundColor: color }} />
                {showLabel && <span className={styles.label}>{label}</span>}
                {showValue && <strong>{color}</strong>}
            </button>

            {open && createPortal(
                <div className={styles.popover} ref={popoverRef} style={popoverStyle} onPointerDown={(event) => event.stopPropagation()}>
                    {typeof window !== "undefined" && window.EyeDropper && <button type="button" className={styles.eyedropperButton} onClick={pickColorFromPage}><MdColorize /> Selecionar cor na página</button>}
                    <HexColorPicker color={color} onChange={handleChange} />
                    <div className={styles.inputRow}>
                        <span>#</span>
                        <HexColorInput color={color} onChange={handleChange} prefixed={false} />
                        <button
                            type="button"
                            className={`${styles.favoriteButton} ${isFavorite ? styles.favorited : ""}`}
                            onClick={toggleFavoriteColor}
                            title={isFavorite ? "Remover dos favoritos" : "Favoritar cor"}
                            aria-label={isFavorite ? "Remover dos favoritos" : "Favoritar cor"}
                        >
                            {isFavorite ? <MdStar /> : <MdStarBorder />}
                        </button>
                    </div>
                    {favoriteColors.length > 0 &&
                        <div className={styles.section}>
                            <span>Favoritas</span>
                            <div className={styles.presets}>
                                {favoriteColors.map(preset => renderColorButton(preset, { removable: true }))}
                            </div>
                        </div>
                    }
                    <div className={styles.presets}>
                        {colors.map(preset => renderColorButton(preset))}
                    </div>
                </div>,
                document.body
            )}
        </div>
    )
}

export default ColorPicker
